import type { EmptyReturnBoardItem, EmptyReturnBoardListQuery } from '@chukua/common';
import { Button, Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import { jobPath } from '../../jobs/jobs.paths';
import { describeDaysRemaining, levelTagColor } from '../policies/deadline-labels';
import type { DeadlineFilterOption } from '../hooks/use-deadline-filter-options';

interface BuildDeadlinesColumnsArgs {
  list: Pick<ListQueryControls<EmptyReturnBoardListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  customerOptions: DeadlineFilterOption[];
  levelOptions: DeadlineFilterOption[];
  onMarkReturned: (item: EmptyReturnBoardItem) => void;
}

/** Each column's `key` is its `sort` field or filter param in `emptyReturnBoardListQuerySchema`; sorting by deadline is sorting by days remaining. */
export function buildDeadlinesColumns({
  list,
  customerOptions,
  levelOptions,
  onMarkReturned,
}: BuildDeadlinesColumnsArgs): TableColumnsType<EmptyReturnBoardItem> {
  return [
    {
      title: 'Container',
      dataIndex: 'containerNumber',
      key: 'containerNumber',
      sorter: true,
      sortOrder: list.sortOrderFor('containerNumber'),
      render: (containerNumber: string) => <span className="font-mono text-xs">{containerNumber}</span>,
    },
    {
      title: 'Job',
      dataIndex: 'jobNumber',
      key: 'jobNumber',
      render: (jobNumber: string, item) => (
        <Link className="font-medium text-link hover:underline" to={jobPath(item.jobId)}>
          {jobNumber}
        </Link>
      ),
    },
    {
      title: 'Customer',
      key: 'customerId',
      filters: customerOptions,
      filteredValue: list.filteredValueFor('customerId'),
      render: (_, item) => item.customerName,
    },
    { title: 'Depot', dataIndex: 'depotZoneCode', key: 'depotZoneCode', render: (code: string) => <span className="font-mono text-xs">{code}</span> },
    {
      title: 'Deadline',
      dataIndex: 'deadline',
      key: 'deadline',
      sorter: true,
      sortOrder: list.sortOrderFor('deadline'),
      render: (deadline: string) => new Date(deadline).toLocaleDateString(),
    },
    { title: 'Days remaining', dataIndex: 'daysRemaining', key: 'daysRemaining', render: (daysRemaining: number) => describeDaysRemaining(daysRemaining) },
    {
      title: 'Level',
      key: 'levelId',
      filters: levelOptions,
      filteredValue: list.filteredValueFor('levelId'),
      render: (_, item) => <Tag color={levelTagColor(item.level)}>{item.level?.label ?? 'On track'}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      render: (_, item) => (
        <Button size="small" onClick={() => onMarkReturned(item)}>
          Mark returned
        </Button>
      ),
    },
  ];
}
